import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

const AdminCalendar = ({ offers }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('schedule'); // 'schedule', 'calendar', 'completed'
  const [currentDate, setCurrentDate] = useState(new Date());

  const monthNames = [
    "Januar", "Februar", "Mart", "April", "Maj", "Juni",
    "Juli", "August", "Septembar", "Oktobar", "Novembar", "Decembar"
  ];


  const todayMidnight = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  // Group events by month for the schedule view
  const groupedOffers = useMemo(() => {
    const filtered = [...offers]
      .filter(offer => {
        if (!offer.datum) return false;
        const offerDate = new Date(offer.datum);
        offerDate.setHours(0, 0, 0, 0);
        
        // Only show future or today's events that aren't marked as completed
        return offerDate >= todayMidnight && 
               offer.status !== 'completed' && 
               offer.status !== 'završeno';
      })
      .sort((a, b) => new Date(a.datum) - new Date(b.datum));

    const groups = {};
    filtered.forEach(offer => {
      const date = new Date(offer.datum);
      const monthKey = `${date.getFullYear()}-${date.getMonth()}`;
      if (!groups[monthKey]) {
        groups[monthKey] = {
          month: date.getMonth(),
          year: date.getFullYear(),
          events: []
        };
      }
      groups[monthKey].events.push(offer);
    });
    return Object.values(groups);
  }, [offers, todayMidnight]);

  // Completed projects (manually marked OR dates that have passed)
  const completedOffers = useMemo(() => {
    return [...offers]
      .filter(offer => {
        const isManuallyCompleted = offer.status === 'completed' || offer.status === 'završeno';
        
        if (!offer.datum) return isManuallyCompleted;
        
        const offerDate = new Date(offer.datum);
        offerDate.setHours(0, 0, 0, 0);
        
        const isPastDate = offerDate < todayMidnight;
        
        return isManuallyCompleted || isPastDate;
      })
      .sort((a, b) => {
        if (!a.datum) return 1;
        if (!b.datum) return -1;
        return new Date(b.datum) - new Date(a.datum);
      });
  }, [offers, todayMidnight]);

  // Calendar logic
  const daysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
  const firstDayOfMonth = (year, month) => {
    const day = new Date(year, month, 1).getDay();
    // Adjust to make Monday the first day (0=Mon, 6=Sun)
    return day === 0 ? 6 : day - 1;
  };

  const prevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const days = [];
  const startDay = firstDayOfMonth(year, month);
  const totalDays = daysInMonth(year, month);

  // Pad beginning
  for (let i = 0; i < startDay; i++) {
    days.push(null);
  }

  for (let d = 1; d <= totalDays; d++) {
    days.push(d);
  }

  const getOffersForDate = (day) => {
    if (!day) return [];
    // Ensure date comparison matches YYYY-MM-DD
    const dateString = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return offers.filter(o => o.datum === dateString);
  };

  return (
    <div className="p-4 md:p-8 font-sans max-w-6xl mx-auto">
      <div className="mb-8">
        <h2 className="text-2xl font-black text-black tracking-tight mb-2 uppercase">Kalendar & Raspored</h2>
        <p className="text-gray-500 text-sm font-medium">Pregledajte sve zakazane termine i vjenčanja.</p>
      </div>

      <div className="flex flex-wrap gap-2 p-1.5 bg-gray-100 rounded-xl w-fit mb-8 border border-gray-200">
        <button
          onClick={() => setActiveTab('schedule')}
          className={`px-8 py-2.5 rounded-lg text-[11px] uppercase tracking-widest font-black transition-all ${activeTab === 'schedule' ? 'bg-black text-white shadow-lg' : 'text-gray-500 hover:text-black hover:bg-white/50'}`}
        >
          Raspored
        </button>
        <button
          onClick={() => setActiveTab('calendar')}
          className={`px-8 py-2.5 rounded-lg text-[11px] uppercase tracking-widest font-black transition-all ${activeTab === 'calendar' ? 'bg-black text-white shadow-lg' : 'text-gray-500 hover:text-black hover:bg-white/50'}`}
        >
          Kalendar
        </button>
        <button
          onClick={() => setActiveTab('completed')}
          className={`px-8 py-2.5 rounded-lg text-[11px] uppercase tracking-widest font-black transition-all ${activeTab === 'completed' ? 'bg-black text-white shadow-lg' : 'text-gray-500 hover:text-black hover:bg-white/50'}`}
        >
          Završeni
        </button>
      </div>

      {activeTab === 'schedule' ? (
        <div className="space-y-12">
          {groupedOffers.length > 0 ? (
            groupedOffers.map((group) => (
              <div key={`${group.year}-${group.month}`} className="space-y-4">
                <div className="flex items-center gap-4">
                  <h3 className="text-sm font-black text-black uppercase tracking-[0.3em] bg-white px-4 py-2 border-2 border-black rounded-lg shadow-sm">
                    {monthNames[group.month]} {group.year}
                  </h3>
                  <div className="flex-1 h-[2px] bg-gray-100"></div>
                </div>
                
                <div className="space-y-3">
                  {group.events.map((offer) => (
                    <div key={offer.firestoreId} className="bg-white border border-gray-100 p-5 rounded-2xl shadow-sm hover:shadow-md transition-all flex flex-col md:flex-row md:items-center justify-between gap-6 group">
                      <div className="flex items-center gap-5">
                        <div className="bg-black text-white p-4 rounded-xl flex flex-col items-center justify-center min-w-[80px] group-hover:scale-105 transition-transform">
                          <span className="text-[10px] uppercase font-bold tracking-widest opacity-70 mb-1">
                            {monthNames[new Date(offer.datum).getMonth()].substring(0, 3)}
                          </span>
                          <span className="text-2xl font-black leading-none">{new Date(offer.datum).getDate()}</span>
                          <span className="text-[9px] uppercase font-bold mt-1 opacity-50">{new Date(offer.datum).getFullYear()}</span>
                        </div>
                        <div>
                          <h3 className="font-black text-black text-xl mb-1 tracking-tight">{offer.name}</h3>
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                              <span className="text-xs font-bold text-gray-400 uppercase tracking-widest">{offer.nazivPaketa}</span>
                              <span className="w-1 h-1 bg-gray-300 rounded-full"></span>
                              <span className="text-sm font-black text-black">{offer.cijena} {offer.valuta}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className={`px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-[0.15em] ${offer.status === 'confirmed' ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' : 'bg-gray-100 text-gray-500 border border-gray-200'}`}>
                          {offer.status}
                        </span>
                        <button 
                          onClick={() => navigate(`/admin/offers/view/${offer.firestoreId}`)} 
                          className="text-[10px] font-black uppercase tracking-widest text-black bg-white hover:bg-black hover:text-white px-6 py-3 rounded-xl border-2 border-black transition-all"
                        >
                          Detalji
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-20 bg-white rounded-2xl border-2 border-dashed border-gray-100">
              <div className="text-4xl mb-4 grayscale opacity-20">📅</div>
              <p className="text-gray-400 font-bold uppercase tracking-widest text-xs">Nema zakazanih termina.</p>
            </div>
          )}
        </div>
      ) : activeTab === 'calendar' ? (
        <div className="bg-white border border-gray-100 rounded-3xl shadow-sm p-8">
          <div className="flex items-center justify-between mb-10">
            <div>
                <h3 className="text-2xl font-black text-black capitalize tracking-tight">{monthNames[month]} {year}</h3>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] mt-1">Pregled po danima</p>
            </div>
            <div className="flex gap-3">
              <button onClick={prevMonth} className="p-3 hover:bg-black hover:text-white rounded-xl transition-all border-2 border-gray-100 text-black">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15 19l-7-7 7-7" /></svg>
              </button>
              <button onClick={nextMonth} className="p-3 hover:bg-black hover:text-white rounded-xl transition-all border-2 border-gray-100 text-black">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" /></svg>
              </button>
            </div>
          </div>

          <div className="grid grid-cols-7 gap-4">
            {['Pon', 'Uto', 'Sri', 'Čet', 'Pet', 'Sub', 'Ned'].map(d => (
              <div key={d} className="pb-4 text-center text-[10px] font-black text-black uppercase tracking-[0.2em] border-b-2 border-gray-50">{d}</div>
            ))}
            {days.map((day, idx) => {
              const dayOffers = getOffersForDate(day);
              const isToday = day && new Date().getDate() === day && new Date().getMonth() === month && new Date().getFullYear() === year;
              
              return (
                <div key={idx} className={`min-h-[120px] p-3 rounded-2xl transition-all border ${!day ? 'bg-transparent border-transparent' : 'bg-gray-50/30 border-gray-100 hover:bg-white hover:shadow-md hover:border-black/10'}`}>
                  {day && (
                    <div className="h-full flex flex-col">
                      <div className="flex justify-between items-start mb-2">
                        <span className={`text-sm font-black ${isToday ? 'bg-black text-white w-7 h-7 flex items-center justify-center rounded-lg shadow-lg scale-110' : (dayOffers.length > 0 ? 'text-black' : 'text-gray-300')}`}>
                            {day}
                        </span>
                        {dayOffers.length > 0 && (
                            <span className="w-1.5 h-1.5 bg-black rounded-full"></span>
                        )}
                      </div>
                      <div className="flex-1 space-y-1.5 overflow-y-auto max-h-[80px] scrollbar-hide">
                        {dayOffers.map(o => (
                          <button 
                            key={o.firestoreId} 
                            onClick={() => navigate(`/admin/offers/view/${o.firestoreId}`)}
                            className="w-full text-left text-[9px] font-black bg-white border border-gray-200 text-black px-2 py-1.5 rounded-lg leading-tight hover:bg-black hover:text-white hover:border-black transition-all truncate" 
                            title={`${o.name} - ${o.nazivPaketa}`}
                          >
                            {o.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {completedOffers.length > 0 ? (
            completedOffers.map((offer) => (
              <div key={offer.firestoreId} className="bg-white border border-gray-100 p-5 rounded-2xl shadow-sm hover:shadow-md transition-all flex flex-col md:flex-row md:items-center justify-between gap-6 group">
                <div className="flex items-center gap-5">
                  <div className="bg-emerald-500 text-white p-4 rounded-xl flex flex-col items-center justify-center min-w-[80px] group-hover:scale-105 transition-transform shadow-lg shadow-emerald-100">
                    <span className="text-[10px] uppercase font-bold tracking-widest opacity-70 mb-1">
                      {offer.datum ? monthNames[new Date(offer.datum).getMonth()].substring(0, 3) : '---'}
                    </span>
                    <span className="text-2xl font-black leading-none">{offer.datum ? new Date(offer.datum).getDate() : '--'}</span>
                    <span className="text-[9px] uppercase font-bold mt-1 opacity-50">{offer.datum ? new Date(offer.datum).getFullYear() : '----'}</span>
                  </div>
                  <div>
                    <h3 className="font-black text-black text-xl mb-1 tracking-tight">{offer.name}</h3>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                        <span className="text-xs font-bold text-gray-400 uppercase tracking-widest">{offer.nazivPaketa}</span>
                        <span className="w-1 h-1 bg-gray-300 rounded-full"></span>
                        <span className="text-sm font-black text-emerald-600">ZAVRŠENO</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right hidden sm:block">
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Ukupno plaćeno</p>
                    <p className="font-black text-black">{offer.cijena} {offer.valuta}</p>
                  </div>
                  <button 
                    onClick={() => navigate(`/admin/offers/view/${offer.firestoreId}`)} 
                    className="text-[10px] font-black uppercase tracking-widest text-white bg-black hover:bg-gray-800 px-6 py-3 rounded-xl border-2 border-black transition-all"
                  >
                    Pregledaj
                  </button>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-20 bg-white rounded-2xl border-2 border-dashed border-gray-100">
              <div className="text-4xl mb-4 grayscale opacity-20">✅</div>
              <p className="text-gray-400 font-bold uppercase tracking-widest text-xs">Nema završenih projekata.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminCalendar;
